import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useMemo, useState } from "react";

import type { IOpenSERPParams } from "app/pages/digital-marketing/keywords-campaigns/reports/shared/Table/TableUtils/RankTrackerTablesTypes";
import { useSerpSnapshotQuery } from "app/pages/digital-marketing/keywords-campaigns/shared/hooks";
import { allTrackers } from "app/services/track/track";

import { DEFAULT_SERP_OVERLAY_TRACKING_EVENT } from "./constants";
import type { SerpSnapshotData } from "./types";

type SerpOverlayContext = {
    isOpen: boolean;
    serpParams: IOpenSERPParams | undefined;
    snapshotData: SerpSnapshotData | undefined;
    isLoading: boolean;
    hasError: boolean;
    activePageIndex: number;
    iframeErrorsByPageIndex: Record<number, boolean>;
    openSerpOverlay: (params: IOpenSERPParams, trackingEventName?: string) => void;
    closeSerpOverlay: () => void;
    handlePageTabClick: (pageNumber: number) => void;
    handleIframeError: (pageIndex: number) => void;
};

const SerpOverlayContext = createContext<SerpOverlayContext | undefined>(undefined);

export const useSerpOverlayContext = (): SerpOverlayContext => {
    const context = useContext(SerpOverlayContext);
    if (!context) {
        throw new Error("useSerpOverlayContext must be used within SerpOverlayProvider");
    }
    return context;
};

type Props = {
    children: ReactNode;
};

export const SerpOverlayProvider = ({ children }: Props) => {
    const [isOpen, setIsOpen] = useState(false);
    const [serpParams, setSerpParams] = useState<IOpenSERPParams | undefined>(undefined);
    const [activePageIndex, setActivePageIndex] = useState(0);
    const [iframeErrorsByPageIndex, setIframeErrorsByPageIndex] = useState<Record<number, boolean>>({});

    const {
        data: snapshotData,
        isPending,
        isError,
    } = useSerpSnapshotQuery({
        serpParams,
        enabled: isOpen && !!serpParams,
    });

    const openSerpOverlay = useCallback(
        (params: IOpenSERPParams, trackingEventName = DEFAULT_SERP_OVERLAY_TRACKING_EVENT) => {
            allTrackers.dispatchTrackEvent("Internal Link", "click", trackingEventName);
            setSerpParams(params);
            setActivePageIndex(0);
            setIframeErrorsByPageIndex({});
            setIsOpen(true);
        },
        [],
    );

    const resetState = useCallback(() => {
        setSerpParams(undefined);
        setActivePageIndex(0);
        setIframeErrorsByPageIndex({});
    }, []);

    const closeSerpOverlay = useCallback(() => {
        setIsOpen(false);
        resetState();
    }, [resetState]);

    const handlePageTabClick = useCallback(
        (pageNumber: number) => {
            if (pageNumber === activePageIndex + 1) {
                return;
            }
            allTrackers.dispatchTrackEvent("Internal Link", "click", `View SERP/Page ${pageNumber}`);
            setActivePageIndex(pageNumber - 1);
        },
        [activePageIndex],
    );

    const handleIframeError = useCallback((pageIndex: number) => {
        setIframeErrorsByPageIndex((currentErrors) => ({
            ...currentErrors,
            [pageIndex]: true,
        }));
    }, []);

    const value = useMemo<SerpOverlayContext>(
        () => ({
            isOpen,
            serpParams,
            snapshotData,
            isLoading: isPending,
            hasError: isError,
            activePageIndex,
            iframeErrorsByPageIndex,
            openSerpOverlay,
            closeSerpOverlay,
            handlePageTabClick,
            handleIframeError,
        }),
        [
            isOpen,
            serpParams,
            snapshotData,
            isPending,
            isError,
            activePageIndex,
            iframeErrorsByPageIndex,
            openSerpOverlay,
            closeSerpOverlay,
            handlePageTabClick,
            handleIframeError,
        ],
    );

    return <SerpOverlayContext.Provider value={value}>{children}</SerpOverlayContext.Provider>;
};
